import styled from 'styled-components';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { useGetKadrySearchCostQuery } from '@shared/api';
import {
  KADRY_DEFAULT_SCHEDULE_HOURS,
  kadryCurrentPackage,
  kadryDate,
  kadryPlural,
  kadrySearchDailyMax,
} from '@entities/kadry';
import { KadryFieldHint, KadryMuted } from '../ui';

const num = new Intl.NumberFormat('ru-RU');
const ratioFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

/** «в 2 раза», «в 2,4 раза», «в 12 раз». */
function times(x: number): string {
  const r = Math.round(x * 10) / 10;
  const word = Number.isInteger(r) ? kadryPlural(r, ['раз', 'раза', 'раз']) : 'раза';
  return `в ${ratioFmt.format(r)} ${word}`;
}

const Wrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

/**
 * Расчёт расхода запросов API hh при выбранном периоде повтора поиска: за
 * сутки, за 30 дней, против 12 ч по умолчанию и на сколько хватит пакета.
 * Чаще 12 ч — предупреждение.
 */
export function ScheduleCost({ hours }: { hours: number }) {
  const { data } = useGetKadrySearchCostQuery();
  const perRun = data?.hh_requests_per_run ?? 0;
  if (!perRun) return null;
  const perDay = kadrySearchDailyMax(perRun, hours);
  const base = kadrySearchDailyMax(perRun, KADRY_DEFAULT_SCHEDULE_HOURS);
  const pkg = data?.hh_package;

  const parts = [
    `Один прогон поиска на hh тратит до ${num.format(perRun)} запросов из пакета API.`,
    `Повтор каждые ${hours} ч — до ${num.format(perDay)} в сутки и до ${num.format(perDay * 30)} за 30 дней на эту заявку.`,
  ];
  const cur = kadryCurrentPackage(data?.hh_packages ?? []);
  if (cur && (data?.hh_packages.length ?? 0) > 1) {
    // Пакетов несколько (у каждого доступа к базе — свой): в сумме не видно,
    // что тот, из которого hh списывает сейчас, кончается.
    const days = Math.floor(cur.remaining / perDay);
    parts.push(
      `Сейчас hh списывает из пакета${cur.expires_at ? ` до ${kadryDate(cur.expires_at)}` : ''}: осталось ${num.format(cur.remaining)} из ${num.format(cur.limit)} — одной такой заявке на ${num.format(days)} ${kadryPlural(days, ['день', 'дня', 'дней'])}. Всего во всех пакетах — ${num.format(pkg?.remaining ?? cur.remaining)}.`,
    );
  } else if (pkg) {
    const days = Math.floor(pkg.remaining / perDay);
    const left = pkg.until ? Math.floor((Date.parse(pkg.until) - Date.now()) / 86_400_000) : null;
    const until = pkg.until ? ` до ${kadryDate(pkg.until)}` : '';
    const limit = pkg.limit != null ? ` из ${num.format(pkg.limit)}` : '';
    const enough =
      left != null && days >= left
        ? 'одной такой заявке его хватит до конца срока пакета'
        : `одной такой заявке его хватит примерно на ${num.format(days)} ${kadryPlural(days, ['день', 'дня', 'дней'])}`;
    parts.push(`В пакете hh осталось ${num.format(pkg.remaining)}${limit}${until}: ${enough}.`);
  }
  parts.push('Повторные прогоны обычно тратят меньше: уже разобранные резюме снова не открываются.');

  if (hours < KADRY_DEFAULT_SCHEDULE_HOURS) {
    return (
      <Wrap>
        <FallbackBanner
          tone="warning"
          title={`Расход запросов hh вырастет ${times(perDay / base)} против повтора раз в ${KADRY_DEFAULT_SCHEDULE_HOURS} ч`}
        >
          {parts.join(' ')}
        </FallbackBanner>
      </Wrap>
    );
  }
  if (hours > KADRY_DEFAULT_SCHEDULE_HOURS) {
    parts.push(`Это ${times(base / perDay)} меньше, чем при повторе раз в ${KADRY_DEFAULT_SCHEDULE_HOURS} ч.`);
  }
  return (
    <Wrap>
      <KadryFieldHint>{parts.join(' ')}</KadryFieldHint>
    </Wrap>
  );
}

/** Коротко для карточки заявки: «до 90 запросов hh в сутки». */
export function ScheduleDailyNote({ hours }: { hours: number }) {
  const { data } = useGetKadrySearchCostQuery();
  const perRun = data?.hh_requests_per_run ?? 0;
  if (!perRun) return null;
  return (
    <KadryMuted as="span">до {num.format(kadrySearchDailyMax(perRun, hours))} запросов hh в сутки</KadryMuted>
  );
}
